import React from "react";
import gql from "graphql-tag";
import { useQuery } from "@apollo/client";
import { LoadingPage } from "../UI";
import Button from "../UI/Button";
import DateDisplay from "../UI/Date";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { prefixImageUrl } from "../utils/imageUrl";
import UISlider from "../UI/Slider";
import 'swiper/swiper-bundle.css';
import LazyLoad from 'react-lazyload';

const Post = styled("article")`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  flex-wrap: wrap;
  border-radius: 1rem;
  overflow: hidden;
  box-sizing: border-box;
  background: white;
  height: 100%;

  .image-column {
    flex: 1;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    min-height: 200px;
    position: relative;
    ${props => props.theme.bp.below.md} {
      flex-basis: 100%;
      height: 200px;
      min-height: auto;
      min-width: 30%;
    }
  }
  .content-column {
    background: white;
    flex: 2;
    padding: 2rem;
    display: flex;
    flex-direction: column;

    ${props => props.theme.bp.below.lg} {
      padding: 1rem;
    }
    ${props => props.theme.bp.below.md} {
       flex-basis: 100%;
    }

    .inner {
      display: flex;
      flex-direction: column;
      color: initial;
      text-decoration: none;
      flex-grow: 1;
    }
    time {
      margin-bottom: .5rem;
      text-transform: uppercase;
    }

    h3 {
      margin-bottom: .5rem;
      font-size: 1.1rem;
      color: inherit;
    }
     p {
       font-size: 0.9rem;
       line-height: 1.4;
       color: #555;
       flex-grow: 1;
       margin-bottom: 1rem;
     }
  }

  &.no-image {
    .content-column {
      flex-basis: 100%;
    }
     ${props => props.theme.bp.below.md} {
        flex-direction: column;
     }
  }

  ${props => props.theme.bp.below.md} {
     flex-direction: column;
  }
`;

const NewsGridContainer = styled("div")`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
  padding: 2rem;

  ${props => props.theme.bp.below.lg} {
    padding: 1rem;
  }
`;

const News = styled("section")`
  width: 100%;
  padding: 2rem 0;
  background: #f7f7f7;
  ${props => props.theme.bp.below.lg} {
    padding: 1rem 0;
  }
`;

const PageTitle = styled("h1")`
  font-size: 2rem;
  margin-bottom: 0;
  padding: 0 2rem;
  text-align: center;
  ${props => props.theme.bp.below.lg} {
    padding: 0 1rem;
  }
`;

const LoadMoreButton = styled(Button)`
  background: ${props => props.theme.colors.primary};
  color: white;
  transition: opacity 0.3s ease;
  &:hover {
    opacity: 0.8;
  }
`;

export const SinglePost = ({ post }) => {
  let content =
    post.content.length > 150
      ? post.content.substring(0, 150) + "..."
      : post.content;

  content = content.replace(/<[^>]*>/g, '');
  const hasImage = post.image?.url;

  return (
    <Post
      className={!hasImage ? "no-image" : ""}
      key={post.slug}
    >
      {hasImage && (
        <div className="image-column" style={{ backgroundImage: `url(${prefixImageUrl(post.image.url)})` }} />
      )}
      <div className="content-column">
        <div className="inner">
          <DateDisplay timestamp={post.time.timestamp} />
          <h3>{post.title}</h3>
          <p
            dangerouslySetInnerHTML={{
              __html: content,
            }}
          />
        </div>
      </div>
    </Post>
  );
};

const NewsListContent = (props) => {
  const { allPosts, displayMode, loadMore, hasNextPage, title, excludeCurrent } = props;
  let posts = allPosts.edges;
  if (excludeCurrent) {
    posts = posts.filter(p => p.node.title !== excludeCurrent);
  }

  const newsSwiperConfig = {
    slidesPerView: 2.7,
    breakpoints: {
      320: { slidesPerView: 1.1, spaceBetween: 10 },
      768: { slidesPerView: 2.2, spaceBetween: 20 },
      1024: { slidesPerView: 2.7, spaceBetween: 30 },
    }
  };

  

  const renderPosts = () => posts.map((p, i) => {
    let post = p.node;
    return (
      <LazyLoad key={i} height={200} offset={100} once>
        <Link to={"/nyheter/" + post.slug} style={{ textDecoration: 'none'}}>
          <SinglePost post={post} />
        </Link>
      </LazyLoad>
    );
  });

  return (
    <News>
      {displayMode === 'grid' ? (
        <>
          <PageTitle>Nyheter</PageTitle>
          <NewsGridContainer>
            {renderPosts()}
          </NewsGridContainer>
          {hasNextPage && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <LoadMoreButton onClick={loadMore}>Ladda fler nyheter</LoadMoreButton>
            </div>
          )}
        </>
      ) : (
        <UISlider title={title} swiperConfig={newsSwiperConfig}>
          {renderPosts()}
        </UISlider>
      )}
    </News>
  );
};

const NewsList = ({ first = 9, displayMode = 'grid', title = "Senaste nytt", excludeCurrent = null}) => {
  const { loading, error, data, fetchMore } = useQuery(query, {
    variables: { first, after: null },
  });

  if (loading && !data) return <LoadingPage />;
  if (error) {
    console.error("GraphQL Error:", error);
    return <p>Error loading news.</p>;
  }
  if (!data || !data.allPosts || !data.allPosts.edges || data.allPosts.edges.length === 0) {
    return null;
  }

  const { allPosts } = data;
  const { pageInfo } = allPosts;

  const loadMore = () => {
    fetchMore({
      variables: {
        after: pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          allPosts: {
            ...fetchMoreResult.allPosts,
            edges: [...prev.allPosts.edges, ...fetchMoreResult.allPosts.edges],
          },
        };
      },
    });
  };

  return (
    <NewsListContent
      allPosts={allPosts}
      displayMode={displayMode}
      loadMore={loadMore}
      hasNextPage={pageInfo.hasNextPage}
      title={title}
      excludeCurrent={excludeCurrent}
    />
  );
};

const query = gql`
  query LatestPosts($first: Int, $after: String) {
    allPosts(first: $first, after: $after) {
      edges {
        node {
          title
          slug
          content
          image {
            url
          }
          time {
            timestamp
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;
export default NewsList;
